import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import enUS from 'antd/locale/en_US';
import { useTranslation } from 'react-i18next';
import App from './App';
import { useThemeStore, getAntdTheme } from './stores/themeStore';
import ChunkLoadErrorBoundary from './components/ChunkLoadErrorBoundary';
import './i18n';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const AppWithTheme: React.FC = () => {
  const mode = useThemeStore((state) => state.mode);
  const setMode = useThemeStore((state) => state.setMode);
  const density = useThemeStore((state) => state.density);
  const { i18n } = useTranslation();
  // Подключаем локаль Ant Design для встроенных компонентов (Pagination
  // "X / page", DatePicker, Empty "No data", etc.). Должна следовать
  // выбранному i18n-языку, иначе UI смешивает en+ru.
  const antdLocale = i18n.resolvedLanguage?.startsWith('en') ? enUS : ruRU;

  useEffect(() => {
    // Применяем тему при первой загрузке
    const saved = localStorage.getItem('theme-storage');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.state?.mode) {
          setMode(parsed.state.mode);
          document.documentElement.setAttribute('data-theme', parsed.state.mode);
          return;
        }
      } catch (e) {
        // Игнорируем ошибки
      }
    }
    // Применяем текущую тему
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode, setMode]); // Добавили зависимости

  useEffect(() => {
    // Применяем тему при изменении
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  return (
    <ConfigProvider
      theme={getAntdTheme(mode)}
      locale={antdLocale}
      componentSize={density === 'compact' ? 'small' : 'middle'}
    >
      <App />
    </ConfigProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ChunkLoadErrorBoundary>
        <AppWithTheme />
      </ChunkLoadErrorBoundary>
    </QueryClientProvider>
  </React.StrictMode>
);
